import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@lib/supabase/client';

interface ImpersonatedUser {
  id: string;
  email: string;
  name: string | null;
  type: 'paid' | 'special';
  returnPath?: string;
}

interface AdminImpersonationContextType {
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  startImpersonation: (user: ImpersonatedUser) => void;
  stopImpersonation: () => void;
  isAdminMode: boolean; // True when admin is impersonating - allows free actions
}

const AdminImpersonationContext = createContext<AdminImpersonationContextType | undefined>(undefined);

const STORAGE_KEY = 'admin_impersonation';

export const AdminImpersonationProvider = ({ children }: { children: ReactNode }) => {
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(null);

  // Load from sessionStorage on mount — verify admin status from server first
  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return;

    const verifyAndRestore = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { sessionStorage.removeItem(STORAGE_KEY); return; }

        const { data: adminRole } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .maybeSingle();

        if (!adminRole) {
          sessionStorage.removeItem(STORAGE_KEY);
          return;
        }

        setImpersonatedUser(JSON.parse(stored));
      } catch {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    };

    verifyAndRestore();
  }, []);

  const startImpersonation = (user: ImpersonatedUser) => {
    setImpersonatedUser(user);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  };

  const stopImpersonation = () => {
    setImpersonatedUser(null);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  return (
    <AdminImpersonationContext.Provider 
      value={{ 
        isImpersonating: !!impersonatedUser,
        impersonatedUser,
        startImpersonation,
        stopImpersonation,
        isAdminMode: !!impersonatedUser
      }}
    >
      {children}
    </AdminImpersonationContext.Provider>
  );
};

export const useAdminImpersonation = () => {
  const context = useContext(AdminImpersonationContext);
  if (context === undefined) {
    throw new Error('useAdminImpersonation must be used within an AdminImpersonationProvider');
  }
  return context;
};
